/**
 * Landing pública de Pagafácil.
 *
 * Sistema de diseño: paleta andaluza (azul brand, terracota, oliva), sin morados,
 * sin dark mode. Tipografía: Spectral (display) + Hanken Grotesk (cuerpo) +
 * Space Mono (rótulos). Tono "amabilidad institucional con magia tecnológica".
 *
 * Estructura:
 *   1) Header con wordmark
 *   2) Hero editorial — Spectral + tarjeta de preview con progreso
 *   3) Cómo funciona — 3 pasos con números Spectral
 *   4) 13 verticales como lista editorial numerada (mono + Spectral + 1 línea)
 *   5) Confianza (Stripe Connect + papeletas legales)
 *   6) CTA final en banda brand sólida
 *   7) Footer
 */
import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Footer from '../components/Footer';

const VERTICALS = [
  { n: '01', label: 'Papeleta de sitio', sub: 'Hermandades y cofradías. Cada hermano paga su sitio con DNI nominativo.' },
  { n: '02', label: 'Sorteo benéfico de peña', sub: 'Tu peña compra 20 papeletas y cada miembro queda dueño legal de las suyas.' },
  { n: '03', label: 'Porra deportiva', sub: 'Champions, La Liga, Mundial. Pago individual, ranking automático, reparto al ganador.' },
  { n: '04', label: 'Entradas en grupo', sub: 'Cada persona paga su entrada con QR. Tú no adelantas nada.' },
  { n: '05', label: 'Cuotas de asociación', sub: 'Cobra a tus socios sin domiciliación. Bizum o tarjeta, sin app que instalar.' },
  { n: '06', label: 'Regalo conjunto', sub: 'Cumple, despedida, jubilación. Cada uno pone su parte sin reclamaciones.' },
  { n: '07', label: 'Crowdfunding mini', sub: 'Si llegamos al mínimo, sucede. Si no, devolvemos todo. Cero riesgo.' },
  { n: '08', label: 'Cena entre amigos', sub: 'Foto del ticket y la IA divide la cuenta por lo que pidió cada uno.' },
  { n: '09', label: 'Yo adelanté, devolvedme', sub: 'Pagaste tú el restaurante. Cada uno te devuelve su parte sin perseguirles.' },
  { n: '10', label: 'Pon lo que quieras', sub: 'Bote abierto para regalo o donativo. Cada uno aporta lo que pueda.' },
  { n: '11', label: 'Pagos a plazos', sub: 'Viaje en grupo, curso largo. Plazos automáticos con recordatorios.' },
  { n: '12', label: 'División por categoría', sub: 'Excursión escolar: niños 50%, adultos 100%. Cada uno paga lo suyo.' },
  { n: '13', label: 'Lista de espera', sub: 'Cena para 6 personas, se apuntan 8. Si alguien cae, el siguiente entra solo.' },
];

const HOWITWORKS = [
  {
    n: '01',
    title: 'Crea el cobro',
    body: 'Pon título, importe y a cuántas personas. Tarda 30 segundos. Sin registro.',
  },
  {
    n: '02',
    title: 'Comparte el enlace',
    body: 'Te llega al email. Pégalo en el WhatsApp del grupo. Cada persona entra y paga lo suyo.',
  },
  {
    n: '03',
    title: 'El dinero llega a tu cuenta',
    body: 'Vinculas tu cuenta con Stripe en 5 minutos. Al cerrarse, se transfiere solo en 2-3 días.',
  },
];


export default function GroupBuyLanding() {
  useEffect(() => {
    document.title = 'Pagafácil — Cobra a varias personas sin adelantar dinero';

    // PWA manifest específico para esta sección
    const existing = document.querySelector('link[rel="manifest"]');
    const m = document.createElement('link');
    m.rel = 'manifest';
    m.href = '/manifest-pagafacil.json';
    document.head.appendChild(m);

    const setMeta = (name: string, content: string) => {
      let el = document.querySelector(`meta[name="${name}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute('name', name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };
    setMeta(
      'description',
      'Cobro colectivo con pago individual. Comparte un enlace por WhatsApp y cada persona paga su parte con Bizum o tarjeta. Sin app, sin login, sin adelantos.',
    );
    setMeta('theme-color', '#1C5D82');

    return () => {
      m.remove();
      if (existing) document.head.appendChild(existing);
    };
  }, []);

  return (
    <div className="min-h-screen bg-surface text-ink font-sans antialiased">
      {/* Header */}
      <header className="border-b border-line">
        <div className="max-w-6xl mx-auto px-6 py-5 flex items-center justify-between">
          <Wordmark />
          <nav className="hidden md:flex items-center gap-5 text-sm text-ink-700">
            <div className="relative group">
              <button type="button" className="hover:text-ink py-2">Soluciones ▾</button>
              <div className="absolute left-0 top-full pt-2 hidden group-hover:block z-10 min-w-[220px]">
                <div className="bg-paper border border-line rounded-card shadow-card p-2">
                  <Link to="/coordina" className="block px-3 py-2 rounded-lg hover:bg-brand-50">
                    <div className="font-display font-semibold text-ink text-[14px]">Coordina</div>
                    <div className="text-[11.5px] text-ink-500">Saldo común entre todos</div>
                  </Link>
                  <Link to="/propina" className="block px-3 py-2 rounded-lg hover:bg-brand-50">
                    <div className="font-display font-semibold text-ink text-[14px]">Propina</div>
                    <div className="text-[11.5px] text-ink-500">QR para propinas en mesa</div>
                  </Link>
                  <Link to="/oficina" className="block px-3 py-2 rounded-lg hover:bg-brand-50">
                    <div className="font-display font-semibold text-ink text-[14px]">Oficina Fácil</div>
                    <div className="text-[11.5px] text-ink-500">Facturas Verifactu autónomos</div>
                  </Link>
                </div>
              </div>
            </div>
            <a href="#como-funciona" className="hover:text-ink">Cómo funciona</a>
            <Link to="/precio" className="hover:text-ink">Precio</Link>
            <Link to="/sorteos-publicos" className="hover:text-ink">Sorteos abiertos</Link>
            <Link to="/mis-cobros" className="hover:text-ink">Mis cobros</Link>
            <Link
              to="/nuevo"
              className="px-4 py-2 rounded-pill bg-brand text-paper font-semibold hover:bg-brand-700 transition shadow-btn"
            >
              Crear cobro
            </Link>
          </nav>
          <Link
            to="/nuevo"
            className="md:hidden px-3 py-1.5 rounded-pill bg-brand text-paper text-sm font-semibold"
          >
            Crear cobro
          </Link>
        </div>
      </header>

      {/* HERO */}
      <section className="relative">
        <div className="max-w-6xl mx-auto px-6 pt-16 md:pt-24 pb-16 md:pb-24 grid lg:grid-cols-[1.1fr_0.9fr] gap-12 lg:gap-16 items-center">
          {/* Texto */}
          <div>
            <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-ink-500 mb-4">
              · Pagafácil
            </div>
            <h1 className="font-display font-bold text-[44px] sm:text-[54px] lg:text-[64px] leading-[0.98] tracking-[-0.02em] text-ink mb-6">
              Cobra a varias personas
              <br />
              <span className="text-brand">sin adelantar</span> ni un euro.
            </h1>
            <p className="text-[17px] leading-relaxed text-ink-700 max-w-[560px] mb-5">
              Pegas un enlace en el WhatsApp del grupo. Cada persona paga su parte
              con Bizum o tarjeta. Recibe su justificante con su nombre. Y a ti te
              llega el dinero a tu cuenta. Sin app, sin login, sin perseguir a nadie.
            </p>
            <div className="inline-flex items-center gap-2 mb-8 px-3 py-1.5 rounded-pill bg-olive-50 border border-[#C7D5A8]">
              <span className="w-1.5 h-1.5 rounded-full bg-olive" />
              <span className="font-mono text-[11.5px] uppercase tracking-[0.14em] text-olive-700">
                Gratis hasta que alguien pague online
              </span>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 mb-6">
              <Link
                to="/nuevo"
                className="px-7 py-3.5 rounded-xl bg-brand text-paper font-semibold text-[15px] hover:bg-brand-700 transition shadow-btn text-center"
              >
                Crear mi primer cobro
              </Link>
              <a
                href="#como-funciona"
                className="px-7 py-3.5 rounded-xl bg-paper border-[1.5px] border-[#CBD9E2] text-brand font-semibold text-[15px] hover:border-brand transition text-center"
              >
                Cómo funciona
              </a>
            </div>
            <p className="text-[12.5px] text-ink-500">
              Tarda 30 segundos. Sin tarjeta de crédito ni registro.
            </p>
          </div>

          {/* Tarjeta preview */}
          <PreviewCard />
        </div>
      </section>

      {/* Cómo funciona */}
      <section id="como-funciona" className="bg-paper border-t border-line">
        <div className="max-w-6xl mx-auto px-6 py-20 md:py-24">
          <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-ink-500 mb-3">
            · Cómo funciona
          </div>
          <h2 className="font-display font-bold text-[34px] md:text-[42px] leading-[1.05] tracking-[-0.02em] text-ink mb-12 max-w-[640px]">
            Tres pasos. Ni uno más.
          </h2>
          <ol className="grid md:grid-cols-3 gap-10 md:gap-14">
            {HOWITWORKS.map((s) => (
              <li key={s.n} className="border-t border-line-strong pt-6">
                <div className="font-display font-bold text-[44px] leading-none text-brand mb-3">
                  {s.n}
                </div>
                <h3 className="font-display font-semibold text-[22px] text-ink mb-2 tracking-[-0.01em]">
                  {s.title}
                </h3>
                <p className="text-[14.5px] text-ink-700 leading-relaxed">{s.body}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* 13 verticales — lista editorial */}
      <section id="usos" className="border-t border-line">
        <div className="max-w-6xl mx-auto px-6 py-20 md:py-24">
          <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-ink-500 mb-3">
            · Para qué sirve
          </div>
          <h2 className="font-display font-bold text-[34px] md:text-[42px] leading-[1.05] tracking-[-0.02em] text-ink mb-3 max-w-[700px]">
            Trece formas de no adelantar dinero.
          </h2>
          <p className="text-[15px] text-ink-700 max-w-[600px] mb-12">
            La misma fontanería, distinta intención. Desde la papeleta de sitio
            de una hermandad sevillana hasta la cuenta del bar de anoche.
          </p>
          <ul className="divide-y divide-line">
            {VERTICALS.map((v) => (
              <li key={v.n} className="py-5 md:py-6 grid grid-cols-[auto_1fr] gap-5 md:gap-8 items-baseline">
                <span className="font-mono text-[12px] text-ink-500 tracking-[0.1em]">
                  {v.n}
                </span>
                <div>
                  <h3 className="font-display font-semibold text-[20px] md:text-[24px] text-ink tracking-[-0.01em] mb-1">
                    {v.label}
                  </h3>
                  <p className="text-[14.5px] text-ink-700 leading-relaxed max-w-[640px]">
                    {v.sub}
                  </p>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Confianza */}
      <section className="bg-albero border-t border-line-strong">
        <div className="max-w-6xl mx-auto px-6 py-20 md:py-24">
          <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-ink-500 mb-3">
            · Por qué es seguro
          </div>
          <h2 className="font-display font-bold text-[30px] md:text-[36px] leading-[1.1] tracking-[-0.02em] text-ink mb-10 max-w-[640px]">
            Riesgo legal cero. Por diseño.
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-paper rounded-card border border-line p-7 shadow-card">
              <h3 className="font-display font-semibold text-[20px] text-ink mb-2 tracking-[-0.01em]">
                Procesador de pago licenciado
              </h3>
              <p className="text-[14.5px] text-ink-700 leading-relaxed">
                Los pagos los procesa un Entidad de Pago con licencia oficial
                española (MONEI · reg. Banco de España #6911, sustituyendo a
                Stripe). Pagafácil opera como plataforma sin tocar tus fondos.
                Tú eres el titular del dinero en todo momento.
              </p>
            </div>
            <div className="bg-paper rounded-card border border-line p-7 shadow-card">
              <h3 className="font-display font-semibold text-[20px] text-ink mb-2 tracking-[-0.01em]">
                Sorteos benéficos · marco legal
              </h3>
              <p className="text-[14.5px] text-ink-700 leading-relaxed">
                Andalucía Decreto 325/1988 permite papeletas digitales para
                asociaciones inscritas ≥ 3 años. Pagafácil te da el motor,
                <strong> pero no presentamos automáticamente como legales</strong>
                todos los sorteos. Cada entidad organizadora es responsable de
                cumplir su normativa autonómica y sus obligaciones con Hacienda.
              </p>
            </div>
          </div>

          {/* Tono prudente · idea 14 ROADMAP */}
          <div className="mt-8 bg-paper border-l-4 border-[#A89D8E] rounded-r-xl px-6 py-5">
            <h3 className="font-display font-semibold text-[16px] text-ink-700 mb-2 tracking-[-0.01em]">
              Qué <em>no</em> somos
            </h3>
            <p className="text-[13.5px] text-ink-700 leading-relaxed">
              Pagafácil <strong>no sustituye</strong> a tu banco, gestoría ni
              contabilidad oficial. No somos asesoría fiscal ni legal. Tu
              entidad debe gestionar su propia contabilidad, declaración
              de IVA/IS, libros oficiales y cumplimiento RGPD. Nuestros
              justificantes y facturas Verifactu son auditables y firmados
              con cadena SHA-256, pero la responsabilidad última de tus
              obligaciones es tuya.
            </p>
          </div>
        </div>
      </section>

      {/* Precio */}
      <section className="bg-paper border-t border-line">
        <div className="max-w-6xl mx-auto px-6 py-20 md:py-24">
          <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-ink-500 mb-3">
            · Precio
          </div>
          <h2 className="font-display font-bold text-[34px] md:text-[42px] leading-[1.05] tracking-[-0.02em] text-ink mb-4 max-w-[640px]">
            Gratis hasta que alguien pague.
          </h2>
          <p className="text-[15px] text-ink-700 max-w-[600px] mb-10">
            Crear el cobro, dividir el ticket con la IA, mandar los mensajes y ver
            quién falta es siempre gratis. Solo cobramos cuando alguien usa Bizum
            o tarjeta a través nuestro · entre 1,5 % y 5 % según el caso.
          </p>
          <Link
            to="/precio"
            className="inline-block px-7 py-3.5 rounded-xl bg-paper border-[1.5px] border-brand text-brand font-semibold text-[15px] hover:bg-brand hover:text-paper transition"
          >
            Calcular mi coste exacto →
          </Link>
        </div>
      </section>

      {/* CTA final */}
      <section className="bg-brand text-paper">
        <div className="max-w-4xl mx-auto px-6 py-20 md:py-24 text-center">
          <h2 className="font-display font-bold text-[36px] md:text-[48px] leading-[1.05] tracking-[-0.02em] mb-5">
            ¿Listo para empezar?
          </h2>
          <p className="text-[16px] text-paper/85 mb-9 max-w-[520px] mx-auto">
            Tarda 30 segundos. No pedimos tarjeta. No pedimos registro.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link
              to="/nuevo"
              className="inline-block px-9 py-4 rounded-xl bg-paper text-brand font-semibold text-[15px] hover:bg-line transition shadow-card"
            >
              Crear mi pagafácil
            </Link>
            <Link
              to="/empezar"
              className="inline-block px-6 py-3.5 rounded-xl border border-paper/40 text-paper font-semibold text-[14px] hover:bg-paper/10 transition"
            >
              ¿No sabes por dónde? Empezar guiado →
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}


// ─── Componentes auxiliares ───────────────────────────────────────────


function Mark({ size = 22, bg = '#FBF7F0' }: { size?: number; bg?: string }) {
  return (
    <div
      className="rounded-full"
      style={{
        width: size,
        height: size,
        background:
          'conic-gradient(#1C5D82 0 33%, #C2683E 0 66%, #5E7B3E 0 100%)',
        boxShadow: `inset 0 0 0 ${Math.max(3, size / 6)}px ${bg}`,
      }}
      aria-hidden
    />
  );
}


function Wordmark() {
  return (
    <Link
      to="/"
      className="flex items-center gap-3"
      aria-label="Pagafácil · ir al inicio"
    >
      <Mark />
      <span className="font-display font-bold text-[17px] text-ink tracking-[-0.01em]">
        Pagafácil
      </span>
    </Link>
  );
}


function PreviewCard() {
  // Ejemplo ilustrativo de cómo se ve un cobro abierto. No es interactivo.
  return (
    <div className="relative">
      <div className="bg-paper border border-line shadow-elevated rounded-card p-7 md:p-8">
        <div className="flex items-center justify-between mb-1">
          <div className="font-mono text-[10.5px] uppercase tracking-[0.18em] text-ink-500">
            Sorteo benéfico
          </div>
          <span
            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-pill text-[11.5px] font-bold"
            style={{ background: '#E8EEDC', color: '#46602C' }}
          >
            <span className="w-1.5 h-1.5 rounded-full bg-olive" />
            Abierto
          </span>
        </div>
        <h3 className="font-display font-bold text-[22px] text-ink leading-tight tracking-[-0.01em] mb-1">
          Peña Los Pajeros · Rocío 2027
        </h3>
        <p className="text-[13.5px] text-ink-500 mb-6">Organiza: Pepe García</p>

        {/* Progreso */}
        <div className="mb-1 flex items-center justify-between text-[13px]">
          <span className="text-ink-700">12 de 18 hermanos · faltan 6</span>
          <span className="font-semibold text-olive-700">300,00 €</span>
        </div>
        <div className="h-2 bg-line rounded-pill overflow-hidden">
          <div className="h-full bg-olive rounded-pill" style={{ width: '66%' }} />
        </div>

        <div className="border-t border-line-soft pt-5 mt-6 grid grid-cols-2 gap-4 text-[13px]">
          <div>
            <div className="font-mono text-[10.5px] uppercase tracking-[0.14em] text-ink-500 mb-1">
              Cada hermano
            </div>
            <div className="font-display font-semibold text-[22px] text-ink leading-none">
              25,00 €
            </div>
          </div>
          <div>
            <div className="font-mono text-[10.5px] uppercase tracking-[0.14em] text-ink-500 mb-1">
              Cierra el
            </div>
            <div className="font-display font-semibold text-[18px] text-ink leading-none mt-1">
              15 may
            </div>
          </div>
        </div>

        <div className="mt-6 grid grid-cols-2 gap-2">
          <button
            type="button"
            className="px-3 py-2.5 rounded-xl bg-brand-50 border border-[#CBD9E2] text-brand-700 text-[13px] font-semibold"
            disabled
            aria-disabled
          >
            Tarjeta
          </button>
          <button
            type="button"
            className="px-3 py-2.5 rounded-xl bg-paper border border-line-strong text-ink-700 text-[13px] font-semibold"
            disabled
            aria-disabled
          >
            Bizum
          </button>
        </div>
        <button
          type="button"
          className="mt-3 w-full px-3 py-3 rounded-xl bg-brand text-paper text-[14px] font-semibold shadow-btn"
          disabled
          aria-disabled
        >
          Pagar 25,00 €
        </button>
        <p className="mt-3 text-[11.5px] text-ink-500 text-center">
          Sin coste para ti · Recibirás justificante por email
        </p>
      </div>

      {/* Pequeña marca de "previsualización" */}
      <div className="absolute -top-3 -left-3 font-mono text-[10px] uppercase tracking-[0.18em] text-ink-400 bg-surface px-2 py-0.5 border border-line">
        Vista previa
      </div>
    </div>
  );
}
